import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import {
  X,
  User as UserIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  MapPin as MapPinIcon,
  ChevronRight,
  LayoutDashboard,
  ShoppingCart,
  Download,
  Edit3,
  Trash2,
  Type,
  Send,
  FileText
} from 'lucide-react';
import Login from './rightPanel/Login';
import { renderProductionFromDesignJson300DPI, compressDataUrlToLimit, createCompressedPdf } from '../utils/productionRender';

import CreateAccount from './rightPanel/CreateAccount';
import { buildApiUrl, authHeaders } from '../config/api';
import { useDesignContext } from '../hooks/useDesignContext';
import Popup from './Popup';
import { usePopup } from '../hooks/usePopup';
import {
  US_STATES_48,
  formatUsPhoneInput,
  formatUsZipInput,
  isValidUsPhone,
  isValidUsState,
  isValidUsZip,
  normalizeUsStateCode
} from '../utils/usContactAddress';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 3100;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const PanelContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 90vw;
  height: 100vh;
  background: #ffffff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 3101;
  display: flex;
  flex-direction: column;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  animation: ${props => props.$isOpen ? slideIn : slideOut} 0.3s ease;
`;

const Header = styled.div`
  padding: 14px 20px;
  background: #666666;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #5c5c5c;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Content = styled.div`
  flex: 1;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: #f9f9f9;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 14px 18px;
  border: none;
  background: ${props => (props.active ? '#ffffff' : '#f9f9f9')};
  color: ${props => (props.active ? '#1a2b48' : '#4b5563')};
  font-size: 14px;
  font-weight: ${props => (props.active ? '600' : '500')};
  cursor: pointer;
  border-bottom: 2px solid ${props => (props.active ? '#5d7231' : 'transparent')};
  margin-bottom: -1px;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${props => (props.active ? '#ffffff' : '#f0f0f0')};
    color: ${props => (props.active ? '#1a2b48' : '#374151')};
  }
`;

const SupportSection = styled.div`
  padding: 18px 22px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SupportHeading = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
`;

const SupportDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
`;

const SupportButton = styled.button`
  align-self: flex-start;
  padding: 9px 16px;
  border-radius: 8px;
  border: none;
  background: #5d7231;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(93, 114, 49, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(93, 114, 49, 0.38);
    background: #4d5f28;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ActionIconRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ActionIconItem = styled.button<{ $variant?: 'default' | 'primary' | 'danger' | 'success' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border: none;
  background: none;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 26px;
    height: 26px;
    padding: 5px;
    border-radius: 10px;
    background: #f1f5f9;
    color: #475569;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.8), 0 1px 2px rgba(15,23,42,0.12);
    transition: background 0.15s ease, color 0.15s ease;
  }

  &:hover svg {
    background: #e2e8f0;
    color: #5d7231;
  }

  ${props => props.$variant === 'primary' && `
    &:hover svg {
      background: rgba(79, 70, 229, 0.2);
      color: #312e81;
    }
  `}

  ${props => props.$variant === 'danger' && `
    &:hover svg {
      background: rgba(248, 113, 113, 0.2);
      color: #b91c1c;
    }
  `}

  ${props => props.$variant === 'success' && `
    &:hover svg {
      background: rgba(16, 185, 129, 0.2);
      color: #065f46;
    }
  `}
`;


const StatusBadge = styled.div<{ $color: string; $bg: string }>`
  margin-top: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${props => props.$color};
`;

type DesignStageMeta = {
  key: string;
  label: string;
  color: string;
  background: string;
  matchers: string[];
};

const DESIGN_STAGE_DEFINITIONS: DesignStageMeta[] = [
  { key: 'design_created', label: 'Design Created', color: '#0f172a', background: 'rgba(15,23,42,0.08)', matchers: ['design_created', 'saved', 'draft', 'new_design', 'newdesign'] },
  { key: 'in_cart', label: 'In Cart', color: '#4f46e5', background: 'rgba(99,102,241,0.12)', matchers: ['in_cart', 'cart'] },
  { key: 'confirmed_order', label: 'Confirmed Order', color: '#9333ea', background: 'rgba(216,180,254,0.35)', matchers: ['confirmed_order', 'order_confirmed', 'confirmed', 'quotation', 'quote', 'approved'] },
  { key: 'waiting_production', label: 'Paid / Waiting for Production', color: '#0d9488', background: 'rgba(13,148,136,0.18)', matchers: ['waiting_for_production', 'waiting_production', 'paid', 'payment_received', 'payed', 'sales_order'] },
  { key: 'in_production', label: 'In Production', color: '#2563eb', background: 'rgba(37,99,235,0.12)', matchers: ['in_production', 'production'] },
  { key: 'ready_for_shipment', label: 'Ready for Shipment', color: '#f97316', background: 'rgba(249,115,22,0.15)', matchers: ['ready_for_shipment', 'ready_for_shippment', 'ready_to_ship', 'ready_for_shipping'] },
  { key: 'shipped', label: 'Shipped', color: '#10b981', background: 'rgba(16,185,129,0.2)', matchers: ['shipped', 'delivered'] },
];

const normalizeDesignStageValue = (value: any) => {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
};

const getDesignStageMeta = (design: any): DesignStageMeta => {
  if (!design) return DESIGN_STAGE_DEFINITIONS[0];
  const candidates = [
    design.status,
    design.status_label,
    design.statusLabel,
    design.statusText,
    design.order_status,
    design.pipeline_stage,
    design.stage,
    design.cart_status
  ]
    .map(normalizeDesignStageValue)
    .filter(Boolean);

  for (const value of candidates) {
    const match = DESIGN_STAGE_DEFINITIONS.find(def =>
      def.matchers.some(matcher => matcher && value.includes(matcher))
    );
    if (match) return match;
  }
  return DESIGN_STAGE_DEFINITIONS[0];
};

const ContactModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3300;
`;

const ContactModalCard = styled.div`
  width: 520px;
  max-width: 92vw;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ContactModalHeader = styled.div`
  padding: 18px 24px;
  background: #666666;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
`;

const ContactCloseButton = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  font-size: 20px;
  cursor: pointer;
`;

const ContactForm = styled.form`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ContactField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ContactLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
`;

const ContactInput = styled.input`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #5d7231;
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.15);
  }
`;

const ContactTextarea = styled.textarea`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  min-height: 110px;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: #5d7231;
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.15);
  }
`;

const ContactActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const ContactSecondaryButton = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-weight: 600;
  cursor: pointer;
`;

const ContactPrimaryButton = styled.button<{ disabled?: boolean }>`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: #5d7231;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  opacity: ${props => (props.disabled ? 0.6 : 1)};
`;

const ContactSuccess = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: #ecfdf5;
  color: #047857;
  font-size: 13px;
  font-weight: 600;
`;

interface RightPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'login' | 'createAccount' | 'profile' | 'designs';
}

type ViewMode = 'login' | 'createAccount' | 'profile' | 'designs';

const RightPanel: React.FC<RightPanelProps> = ({ isOpen, onClose, initialView }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('login');
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const { currentDesign, updateDesign, setSelectedBackground, setSelectedGraniteImage, setSelectedGraniteMeta, setSelectedGraniteBaseImage, setIsApplyingDesign, setSelectedModelId, incrementTemplateResetKey, setDesignMode, setActiveSide } = useDesignContext();
  const { popupState, hidePopup, showError, showSuccess, showConfirm } = usePopup();
  const [designs, setDesigns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  // Download modal state
  const [downloadOpen, setDownloadOpen] = useState<boolean>(false);
  const [downloadDesign, setDownloadDesign] = useState<any | null>(null);
  const [downloadFormat, setDownloadFormat] = useState<'png' | 'jpg' | 'pdf' | 'kpro'>('png');
  const [downloadSize, setDownloadSize] = useState<'small' | 'large'>('large');
  // Email design modal state
  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [emailModalDesign, setEmailModalDesign] = useState<any | null>(null);
  const [emailToAddress, setEmailToAddress] = useState<string>('');
  const [emailMessage, setEmailMessage] = useState<string>('');
  const [emailSending, setEmailSending] = useState<boolean>(false);
  const [pendingCartDesign, setPendingCartDesign] = useState<any | null>(null);
  const [cartConfirmChecked, setCartConfirmChecked] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState<string | null>(null);
  const [loginForgotIntent, setLoginForgotIntent] = useState<{ active: boolean; email?: string }>({ active: false });

  React.useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
    const parsed = userStr ? (() => { try { return JSON.parse(userStr); } catch { return null; } })() : null;
    setUser(parsed);
    if (token && parsed) setViewMode('profile');

    const onAuthChange = () => {
      const t = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const u = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
      const p = u ? (() => { try { return JSON.parse(u); } catch { return null; } })() : null;
      setUser(p);
      setViewMode(t && p ? 'profile' : 'login');
    };
    window.addEventListener('auth:login', onAuthChange);
    window.addEventListener('auth:logout', onAuthChange);
    return () => {
      window.removeEventListener('auth:login', onAuthChange);
      window.removeEventListener('auth:logout', onAuthChange);
    };
  }, []);
  // Fetch profile when entering profile view
  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/profile'), { headers: { ...authHeaders() } });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.ok) {
          setProfile(data.user);
        }
      } catch { }
    };
    if (viewMode === 'profile') fetchProfile();
  }, [viewMode]);

  const updateProfile = async (changes: any) => {
    setSaving(true);
    try {
      const res = await fetch(buildApiUrl('/api/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(changes)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || 'Update failed');
      setProfile(data.user);
      // also mirror minimal fields to stored auth_user
      try {
        const stored = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user') || null;
        const s = stored ? JSON.parse(stored) : {};
        const merged = { ...s, ...changes };
        localStorage.setItem('auth_user', JSON.stringify(merged));
      } catch { }
      showSuccess('Updated', 'Profile updated successfully.');
    } catch (e: any) {
      showError('Update Error', e?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const chipStyle: React.CSSProperties = { width: 44, height: 44, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', boxShadow: 'inset 0 1px 2px rgba(148,163,184,0.25)' };
  const goBtn: React.CSSProperties = { width: 32, height: 32, borderRadius: 16, border: '1px solid #d1d5db', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666666', cursor: 'pointer', transition: 'all 0.2s ease' };
  const detailRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 14, background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 10, boxShadow: '0 4px 12px rgba(15,23,42,0.05)' };
  const editCardStyle: React.CSSProperties = { padding: '14px 16px', borderRadius: 16, border: '1px solid #e2e8f0', background: '#fff', boxShadow: '0 14px 32px rgba(15,23,42,0.08)', marginBottom: 12 };
  const infoLabelStyle: React.CSSProperties = { fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 600 };
  const infoValueStyle: React.CSSProperties = { fontWeight: 600, color: '#0f172a', fontSize: 15 };
  const nameChipStyle: React.CSSProperties = { ...chipStyle, background: '#e0f2fe', color: '#0284c7' };
  const emailChipStyle: React.CSSProperties = { ...chipStyle, background: '#ede9fe', color: '#7c3aed' };
  const phoneChipStyle: React.CSSProperties = { ...chipStyle, background: '#dcfce7', color: '#16a34a' };
  const addressChipStyle: React.CSSProperties = { ...chipStyle, background: '#fef3c7', color: '#d97706' };

  const NameRow: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [first, setFirst] = useState(profile?.first_name || '');
    const [middle, setMiddle] = useState((profile?.middle_name ?? profile?.middle) || '');
    const [last, setLast] = useState(profile?.last_name || '');
    const [nameError, setNameError] = useState('');
    React.useEffect(() => {
      setFirst(profile?.first_name || '');
      setMiddle((profile?.middle_name ?? profile?.middle) || '');
      setLast(profile?.last_name || '');
    }, [profile]);
    React.useEffect(() => { if (nameError) setNameError(''); }, [first, middle, last]);
    if (!isEditing) {
      const display = [profile?.first_name, (profile?.middle_name ?? profile?.middle), profile?.last_name].filter(Boolean).join(' ') || '-';
      return (
        <div style={detailRowStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={nameChipStyle}><UserIcon size={16} /></div>
            <div>
              <div style={infoLabelStyle}>Name</div>
              <div style={infoValueStyle}>{display}</div>
            </div>
          </div>
          <button onClick={() => setIsEditing(true)} style={goBtn}><ChevronRight size={16} /></button>
        </div>
      );
    }
    return (
      <div style={editCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <div style={nameChipStyle}><UserIcon size={16} /></div>
          <div style={infoLabelStyle}>Name</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" style={{ padding: '12px 12px', border: `1px solid ${nameError && !first.trim() ? '#ef4444' : '#cbd5e1'}`, borderRadius: 10, background: '#f8fafc' }} />
          <input value={middle} onChange={(e) => setMiddle(e.target.value)} placeholder="Middle name" style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }} />
          <input value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" style={{ padding: '12px 12px', border: `1px solid ${nameError && !last.trim() ? '#ef4444' : '#cbd5e1'}`, borderRadius: 10, background: '#f8fafc' }} />
        </div>
        {nameError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{nameError}</div>}
        <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => {
            setFirst(profile?.first_name || '');
            setMiddle((profile?.middle_name ?? profile?.middle) || '');
            setLast(profile?.last_name || '');
            setNameError('');
            setIsEditing(false);
          }} style={{ padding: '8px 14px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 13 }}>Cancel</button>
          <button disabled={saving} onClick={async () => {
            const trimmedFirst = first.trim();
            const trimmedMiddle = middle.trim();
            const trimmedLast = last.trim();
            if (!trimmedFirst || !trimmedLast) {
              setNameError('First and last name are required.');
              return;
            }
            if (trimmedFirst.length > 50 || trimmedMiddle.length > 50 || trimmedLast.length > 50) {
              setNameError('Names must be 50 characters or fewer.');
              return;
            }
            await updateProfile({ first_name: trimmedFirst, middle: trimmedMiddle, last_name: trimmedLast });
            setIsEditing(false);
          }} style={{ padding: '8px 16px', border: 'none', background: '#5d7231', color: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Save</button>
        </div>
      </div>
    );
  };

  const EmailRow: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [email, setEmail] = useState(profile?.email || '');
    const [emailError, setEmailError] = useState('');
    React.useEffect(() => { setEmail(profile?.email || ''); }, [profile]);
    React.useEffect(() => { if (emailError) setEmailError(''); }, [email]);
    if (!isEditing) {
      return (
        <div style={detailRowStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={emailChipStyle}><MailIcon size={16} /></div>
            <div>
              <div style={infoLabelStyle}>Email</div>
              <div style={infoValueStyle}>{email || '-'}</div>
            </div>
          </div>
          <button onClick={() => setIsEditing(true)} style={goBtn}><ChevronRight size={16} /></button>
        </div>
      );
    }
    return (
      <div style={editCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <div style={emailChipStyle}><MailIcon size={16} /></div>
          <div style={infoLabelStyle}>Email</div>
        </div>
        <input value={email} onChange={(e) => setEmail(e.target.value)} style={{ padding: '12px 12px', border: `1px solid ${emailError ? '#ef4444' : '#cbd5e1'}`, borderRadius: 10, width: '100%', marginBottom: 6, background: '#f8fafc' }} />
        {emailError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 4 }}>{emailError}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={() => {
            setEmail(profile?.email || '');
            setEmailError('');
            setIsEditing(false);
          }} style={{ padding: '8px 14px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 13 }}>Cancel</button>
          <button disabled={saving} onClick={async () => {
            const trimmed = email.trim();
            const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
            if (!isValid) {
              setEmailError('Enter a valid email address.');
              return;
            }
            await updateProfile({ email: trimmed });
            setIsEditing(false);
          }} style={{ padding: '8px 16px', border: 'none', background: '#5d7231', color: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Save</button>
        </div>
      </div>
    );
  };

  const PhoneRow: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [phone, setPhone] = useState(formatUsPhoneInput(profile?.phone || ''));
    const [phoneError, setPhoneError] = useState('');
    React.useEffect(() => { setPhone(formatUsPhoneInput(profile?.phone || '')); }, [profile]);
    React.useEffect(() => { if (phoneError) setPhoneError(''); }, [phone]);
    if (!isEditing) {
      return (
        <div style={detailRowStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={phoneChipStyle}><PhoneIcon size={16} /></div>
            <div>
              <div style={infoLabelStyle}>Phone</div>
              <div style={infoValueStyle}>{phone || '-'}</div>
            </div>
          </div>
          <button onClick={() => setIsEditing(true)} style={goBtn}><ChevronRight size={16} /></button>
        </div>
      );
    }
    return (
      <div style={editCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <div style={phoneChipStyle}><PhoneIcon size={16} /></div>
          <div style={infoLabelStyle}>Phone</div>
        </div>
        <input value={phone} onChange={(e) => setPhone(formatUsPhoneInput(e.target.value, phone))} placeholder="+1 (706) 123-4567" style={{ padding: '12px 12px', border: `1px solid ${phoneError ? '#ef4444' : '#cbd5e1'}`, borderRadius: 10, width: '100%', marginBottom: 6, background: '#f8fafc' }} />
        {phoneError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 4 }}>{phoneError}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={() => {
            setPhone(profile?.phone || '');
            setPhoneError('');
            setIsEditing(false);
          }} style={{ padding: '8px 14px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 13 }}>Cancel</button>
          <button disabled={saving} onClick={async () => {
            const trimmed = phone.trim();
            const isValid = isValidUsPhone(trimmed);
            if (!isValid) {
              setPhoneError('Phone must use format +1 (706) 123-4567.');
              return;
            }
            await updateProfile({ phone: trimmed });
            setIsEditing(false);
          }} style={{ padding: '8px 16px', border: 'none', background: '#5d7231', color: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Save</button>
        </div>
      </div>
    );
  };

  const AddressRow: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [a1, setA1] = useState(profile?.address_line1 || '');
    const [a2, setA2] = useState(profile?.address_line2 || '');
    const [city, setCity] = useState(profile?.city || '');
    const [state, setState] = useState(normalizeUsStateCode(profile?.state || ''));
    const [zip, setZip] = useState(formatUsZipInput(profile?.zip_code || ''));
    const [country, setCountry] = useState(profile?.country || '');
    const [addressError, setAddressError] = useState('');
    React.useEffect(() => {
      setA1(profile?.address_line1 || ''); setA2(profile?.address_line2 || ''); setCity(profile?.city || ''); setState(normalizeUsStateCode(profile?.state || '')); setZip(formatUsZipInput(profile?.zip_code || '')); setCountry(profile?.country || '');
    }, [profile]);
    if (!isEditing) {
      const lines = [a1, a2, [city, state].filter(Boolean).join(', '), zip, country].filter(Boolean);
      return (
        <div style={{ ...detailRowStyle, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={addressChipStyle}><MapPinIcon size={16} /></div>
            <div>
              <div style={infoLabelStyle}>Address</div>
              <div style={{ ...infoValueStyle, whiteSpace: 'pre-line' }}>{lines.length ? lines.join('\n') : '-'}</div>
            </div>
          </div>
          <button onClick={() => setIsEditing(true)} style={goBtn}><ChevronRight size={16} /></button>
        </div>
      );
    }
    return (
      <div style={editCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <div style={addressChipStyle}><MapPinIcon size={16} /></div>
          <div style={infoLabelStyle}>Address</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <input value={a1} onChange={(e) => setA1(e.target.value)} placeholder="Address line 1" style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }} />
          <input value={a2} onChange={(e) => setA2(e.target.value)} placeholder="Address line 2" style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }} />
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }} />
          <select value={normalizeUsStateCode(state)} onChange={(e) => setState(e.target.value)} style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }}>
            <option value="">Select state</option>
            {US_STATES_48.map((stateOption) => (
              <option key={stateOption.code} value={stateOption.code}>
                {stateOption.code} - {stateOption.name}
              </option>
            ))}
          </select>
          <input value={zip} onChange={(e) => setZip(formatUsZipInput(e.target.value))} placeholder="12345" inputMode="numeric" maxLength={5} style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }} />
          <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country" style={{ padding: '12px 12px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#f8fafc' }} />
        </div>
        {addressError && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{addressError}</div>}
        <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => {
            setA1(profile?.address_line1 || '');
            setA2(profile?.address_line2 || '');
            setCity(profile?.city || '');
            setState(normalizeUsStateCode(profile?.state || ''));
            setZip(formatUsZipInput(profile?.zip_code || ''));
            setCountry(profile?.country || '');
            setAddressError('');
            setIsEditing(false);
          }} style={{ padding: '8px 14px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: 13 }}>Cancel</button>
          <button disabled={saving} onClick={async () => {
            if (state && !isValidUsState(state)) {
              setAddressError('Please choose a valid U.S. state.');
              return;
            }
            if (zip && !isValidUsZip(zip)) {
              setAddressError('ZIP code must be exactly 5 digits.');
              return;
            }
            await updateProfile({
              address_line1: a1,
              address_line2: a2,
              city,
              state: normalizeUsStateCode(state),
              zip_code: String(zip || '').trim(),
              country
            });
            setAddressError('');
            setIsEditing(false);
          }} style={{ padding: '8px 16px', border: 'none', background: '#5d7231', color: '#fff', borderRadius: 999, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Save</button>
        </div>
      </div>
    );
  };

  // Respond to external requests to open a specific view
  React.useEffect(() => {
    if (!isOpen) return;
    if (initialView) setViewMode(initialView);
  }, [initialView, isOpen]);

  // Listen for external event to switch views (from toolbar button)
  React.useEffect(() => {
    const handler = (e: any) => {
      const v = e?.detail?.view as ViewMode | undefined;
      if (v) setViewMode(v);
    };
    window.addEventListener('rightpanel:open', handler);
    return () => window.removeEventListener('rightpanel:open', handler);
  }, []);


  // When entering designs view, fetch user designs
  React.useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(buildApiUrl('/api/designs'), { headers: { ...authHeaders() } });
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data?.designs) ? data.designs : [];
        setDesigns(list);
      } catch (e: any) {
        setError(e?.message || 'Failed to load designs');
      } finally {
        setLoading(false);
      }
    };
    if (viewMode === 'designs') fetchDesigns();
  }, [viewMode]);

  const refreshDesigns = async () => {
    try {
      const res = await fetch(buildApiUrl('/api/designs'), { headers: { ...authHeaders() } });
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data?.designs) ? data.designs : [];
      setDesigns(list);
    } catch (e: any) {
      showError('Refresh Error', 'Failed to refresh designs');
    }
  };

  const handleCreateQuotation = (design: any) => {
    window.location.href = `/customer/dashboard?tab=saved_designs&checkout=${design.id}`;
  };

  const updateDesignStatus = async (designId: number, status: 'saved' | 'cart') => {
    try {
      const res = await fetch(buildApiUrl(`/api/designs/${designId}/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || 'Unable to update design');
      await refreshDesigns();
      showSuccess('Updated', status === 'cart' ? 'Design moved to cart' : 'Design moved out of cart');
      return true;
    } catch (err: any) {
      showError('Update Error', err?.message || 'Failed to update design');
      return false;
    }
  };

  const renameDesign = async (d: any, nameInput?: string) => {
    const name = (nameInput ?? editingName ?? d?.name ?? 'Untitled').trim() || 'Untitled';
    try {
      await fetch(buildApiUrl(`/api/designs/${d.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name })
      });
      await refreshDesigns();
      setEditingId(null);
    } catch {
      showError('Rename Error', 'Failed to rename design');
    }
  };

  const deleteDesign = async (d: any) => {
    const doDelete = async () => {
      try {
        const res = await fetch(buildApiUrl(`/api/designs/${d.id}`), { method: 'DELETE', headers: { ...authHeaders() } });
        if (!res.ok) throw new Error('Delete failed');

        // If the deleted design is currently loaded, clear its ID from the current design
        if (currentDesign.id === d.id) {
          updateDesign(prev => ({ ...prev, id: undefined }));
        }

        await refreshDesigns();
        showSuccess('Deleted', 'The design was deleted successfully.');
      } catch (e: any) {
        showError('Delete Error', e?.message || 'Failed to delete');
      }
    };

    showConfirm(
      'Delete Design?',
      'Delete this design? This cannot be undone.',
      doDelete,
      () => { },
      'Delete',
      'Cancel'
    );
  };

  const deleteAllDesigns = async () => {
    const doDelete = async () => {
      if (!designs || designs.length === 0) return;
      setLoading(true);
      try {
        // delete sequentially to avoid too many parallel requests
        for (const d of designs) {
          try {
            const res = await fetch(buildApiUrl(`/api/designs/${d.id}`), { method: 'DELETE', headers: { ...authHeaders() } });
            if (!res.ok) {
              const text = await res.text().catch(() => null);
              throw new Error(text || 'Delete failed');
            }

            // If the deleted design is currently loaded, clear its ID from the current design
            if (currentDesign?.id === d.id) {
              updateDesign?.(prev => ({ ...prev, id: undefined }));
            }
          } catch (e: any) {
            // stop on first failure and surface the error
            throw e;
          }
        }

        await refreshDesigns();
        showSuccess('Deleted', 'All designs were deleted successfully.');
      } catch (e: any) {
        showError('Delete Error', e?.message || 'Failed to delete all designs');
      } finally {
        setLoading(false);
      }
    };

    showConfirm(
      'Delete all designs?',
      'Delete all saved designs? This cannot be undone.',
      doDelete,
      () => { },
      'Delete all',
      'Cancel'
    );
  };

  // Helper: prefetch a list of image URLs (with per-image timeout) and resolve when all settle or overall timeout
  const prefetchImages = (urls: string[], perImageTimeout = 5000, overallTimeout = 15000) => {
    const unique = Array.from(new Set((urls || []).filter(Boolean)));
    if (!unique.length) return Promise.resolve({ success: true, fetched: [] as string[] });

    return new Promise<{ success: boolean; fetched: string[] }>((resolve) => {
      const fetched: string[] = [];
      let settled = 0;
      let finished = false;

      const done = (url?: string) => {
        if (url) fetched.push(url);
        settled += 1;
        if (settled >= unique.length && !finished) {
          finished = true;
          resolve({ success: true, fetched });
        }
      };

      const timers: any[] = [];

      unique.forEach((url) => {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          let called = false;
          const onFinish = () => {
            if (called) return;
            called = true;
            clearTimeout(timer);
            done(url);
          };
          img.onload = onFinish;
          img.onerror = onFinish;
          const timer = setTimeout(() => onFinish(), perImageTimeout);
          timers.push(timer);
          // Start load
          try { img.src = url; } catch (e) { onFinish(); }
        } catch (e) {
          // If constructing Image fails, count as finished for that URL
          done();
        }
      });

      // overall timeout
      setTimeout(() => {
        if (!finished) {
          finished = true;
          // clear timers
          timers.forEach(t => clearTimeout(t));
          resolve({ success: false, fetched });
        }
      }, overallTimeout);
    });
  };

  // Unified loader: use this for both card click and three-dot Edit so behavior matches
  const openDesign = async (d: any) => {
    try {
      // Proactively clear previous granite and reset template cache before the new design loads
      try {
        setSelectedGraniteImage?.(null);
        setSelectedGraniteMeta?.(null);
        setSelectedGraniteBaseImage?.(null);
        setActiveSide?.('front');
        incrementTemplateResetKey?.();
        setIsApplyingDesign?.(true);
        // Set global flag to prevent auto-resizing during load
        (window as any).__isApplyingDesign = true;
        (window as any).__initialDesignApplied = true;
        // Close right panel immediately so the preloader is visible while loading
        handleClose();
      } catch { }
      const r = await fetch(buildApiUrl(`/api/designs/${d.id}`), { headers: { ...authHeaders() } });
      const j = await r.json();
      if (r.ok && j?.ok && j.design?.design_json) {
        try {
          const designData = typeof j.design.design_json === 'string' ? JSON.parse(j.design.design_json) : j.design.design_json;

          // Collect image URLs to prefetch: template elements, artworks, background, selectedGraniteMeta
          const urls: string[] = [];
          try {
            // selectedGraniteMeta.src (if provided)
            if (designData?.selectedGraniteMeta?.src) urls.push(designData.selectedGraniteMeta.src);

            // background may be a string or object with src
            if (designData?.background) {
              if (typeof designData.background === 'string') urls.push(designData.background);
              else if (designData.background?.src) urls.push(designData.background.src);
            }

            // elements may contain various image sources
            if (designData.elements && Array.isArray(designData.elements)) {
              designData.elements.forEach((el: any) => {
                // common places where image srcs live
                if (el?.properties?.src) urls.push(el.properties.src);
                if (el?.src) urls.push(el.src);
                if (el?.image) urls.push(el.image);
                // nested shapes or decoration might have image props
                if (el?.properties?.images && Array.isArray(el.properties.images)) {
                  el.properties.images.forEach((it: any) => { if (it?.src) urls.push(it.src); });
                }
              });
            }
          } catch (e) {

          }

          // Prefetch images (per-image timeout 5s, overall timeout 15s). We don't fail the load if some images time out.
          try {
            const pre = await prefetchImages(urls, 5000, 15000);

          } catch (e) {

          }

          // Normalize all elements: preserve saved positions, sizes, and properties
          // Detect template bounds up front (used to infer vase side when missing)
          const templateEl = Array.isArray(designData.elements)
            ? designData.elements.find((el: any) => el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate))
            : null;
          const tplCenterX = templateEl ? ((Number(templateEl.x) || 0) + (Number(templateEl.width) || 0) / 2) : null;

          // Mark all elements as fromSaved to prevent auto-resizing and auto-positioning
          const normalizedElements = Array.isArray(designData.elements)
            ? designData.elements.map((el: any) => {
              const role = String(el?.properties?.role || el?.properties?.kind || '').toLowerCase();
              const isVase = el?.type === 'artwork' && role === 'vase';

              // Preserve all element properties exactly as saved
              // Infer side for vases when missing so autosnap can place left/right correctly
              let inferredSide: 'left' | 'right' | undefined;
              if (isVase && tplCenterX !== null && el?.properties?.side == null) {
                const vaseCenter = (Number(el.x) || 0) + (Number(el.width) || 0) / 2;
                inferredSide = vaseCenter >= tplCenterX ? 'right' : 'left';
              }

              const normalizedEl = {
                ...el,
                // Ensure all position/size properties are preserved
                x: typeof el.x === 'number' ? el.x : 0,
                y: typeof el.y === 'number' ? el.y : 0,
                width: typeof el.width === 'number' && el.width > 0 ? el.width : (el.width || 0),
                height: typeof el.height === 'number' && el.height > 0 ? el.height : (el.height || 0),
                rotation: typeof el.rotation === 'number' ? el.rotation : 0,
                flipX: el.flipX === true,
                flipY: el.flipY === true,
                properties: {
                  ...el.properties,
                  ...(inferredSide ? { side: inferredSide } : {}),
                  // Mark as fromSaved to prevent auto-resizing and auto-positioning
                  fromSaved: true,
                  userResized: true, // Prevent auto-resize on image load
                  // For vases: enable autosnap temporarily so they get repositioned relative to template
                  // This ensures correct positioning even if template was repositioned during load
                  autosnap: isVase ? true : el?.properties?.autosnap,
                }
              };

              // Preserve text-specific properties
              if (el.type === 'text') {
                normalizedEl.boxWidth = typeof (el as any).boxWidth === 'number' ? (el as any).boxWidth : el.width;
              }

              return normalizedEl;
            })
            : designData.elements;

          const normalizedDesign = { ...designData, elements: normalizedElements };

          const inferredMode: 'LSP/QD' | 'ESQ' = (() => {
            const topMode = String(designData?.designMode || designData?.mode || '').toUpperCase();
            if (topMode === 'ESQ') return 'ESQ';
            if (topMode === 'LSP/QD' || topMode === 'LSP' || topMode === 'LSP-QD') return 'LSP/QD';
            if (Number(designData?.selectedEsqModalId || 0) > 0) return 'ESQ';
            const els = Array.isArray(designData?.elements) ? designData.elements : [];
            for (const el of els) {
              const p = (el && el.properties) || {};
              const m = String(p?.designMode || '').toUpperCase();
              if (m === 'ESQ' || Number(p?.esqModalId || 0) > 0 || String(p?.esqModalName || '').trim()) return 'ESQ';
            }
            return 'LSP/QD';
          })();

          try {
            setDesignMode?.(inferredMode, true);
            localStorage.setItem('design_tool.mode', inferredMode);
            sessionStorage.setItem('design_tool.page_mode', inferredMode);
          } catch { }

          const isQuotedInRightPanel = Boolean(d?.is_quoted || d?.status === 'quoted' || j.design?.is_quoted || j.design?.status === 'quoted');

          // Apply the design and ensure DB id/name are preserved in state so first save can UPDATE
          // IMPORTANT: Apply design FIRST before restoring granite meta to preserve template position
          updateDesign(() => ({
            ...normalizedDesign,
            activeSide: 'front',
            id: d.id, // ensure the id from the record is present
            name: j.design?.name || designData?.name || 'Untitled',
            is_quoted: isQuotedInRightPanel,
            status: isQuotedInRightPanel ? 'quoted' : (j.design?.status || d?.status || 'saved')
          }));

          // Small delay to ensure design is applied before restoring granite meta
          // This prevents template from being replaced/repositioned
          await new Promise(resolve => setTimeout(resolve, 50));

          // Restore granite meta/image. Ensure saved granite meta is accurately restored so
          // upright monument shape, base, and vases render correctly after page refresh.
          try {
            const tmpl = normalizedElements.find((el: any) =>
              el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate)
            );
            const meta = designData.selectedGraniteMeta || (tmpl && tmpl.properties && tmpl.properties.src ? {
              modelId: tmpl.properties.modelId || 0,
              sizeId: tmpl.properties.sizeId || 0,
              colorId: tmpl.properties.colorId || 0,
              src: tmpl.properties.src
            } : null);

            if (meta) {
              setSelectedGraniteMeta?.(meta);
              if (meta.src) {
                setSelectedGraniteImage?.(meta.src);
              }
            } else {
              setSelectedGraniteImage?.(null);
              setSelectedGraniteMeta?.(null);
            }

            // Restore granite base image for both LSP/QD and ESQ modes (e.g. Serptop Markers with base)
            const savedBaseImage = designData.selectedGraniteBaseImage || (normalizedDesign as any)?.selectedGraniteBaseImage;
            if (savedBaseImage) {
              setSelectedGraniteBaseImage?.(savedBaseImage);
            } else if (inferredMode === 'ESQ') {
              const modalId = Number(
                designData.selectedEsqModalId ||
                designData.selectedModelId ||
                normalizedElements.find((el: any) => el?.properties?.esqModalId || el?.properties?.modalId)?.properties?.esqModalId ||
                0
              );
              if (modalId > 0) {
                try {
                  const res = await fetch(buildApiUrl(`/api/esq/modal/${modalId}`));
                  if (res.ok) {
                    const modal = await res.json();
                    if (modal && modal.base_path) {
                      const baseSrc = buildApiUrl('/api/uploads/' + String(modal.base_path).replace(/^\/?uploads\//, ''));
                      setSelectedGraniteBaseImage?.(baseSrc);
                    } else {
                      setSelectedGraniteBaseImage?.(null);
                    }
                  } else {
                    setSelectedGraniteBaseImage?.(null);
                  }
                } catch {
                  setSelectedGraniteBaseImage?.(null);
                }
              } else {
                setSelectedGraniteBaseImage?.(null);
              }
            } else {
              setSelectedGraniteBaseImage?.(null);
            }
          } catch (e) {

          }

          // Restore background like card click
          if (designData.background) {

            // Normalize background to expected shape { image_url, thumb_url?, id?, name? }
            let bg = designData.background;
            if (typeof bg === 'string') {
              bg = { image_url: bg, name: 'Background', id: undefined };
            } else if (bg?.src && !bg.image_url) {
              bg = { ...bg, image_url: bg.src };
            }
            setSelectedBackground(bg);
            try {
              localStorage.setItem('design_tool.background', JSON.stringify(bg));
              localStorage.setItem(`design_tool.background.${inferredMode}`, JSON.stringify(bg));
            } catch (e) {

            }
          } else {
            setSelectedBackground(null);
          }

        } finally {
        }
      } else {
        showError('Load Error', 'Failed to load design');
      }
    } catch (e: any) {
      showError('Load Error', e?.message || 'Failed to load design');
    } finally {
      try {
        window.setTimeout(() => {
          try { (window as any).__isApplyingDesign = false; } catch { }
          try { setIsApplyingDesign?.(false); } catch { }
        }, 0);
      } catch {
        try { (window as any).__isApplyingDesign = false; } catch { }
        try { setIsApplyingDesign?.(false); } catch { }
      }
    }
  };

  React.useEffect(() => {
    try {
      const resumeId = localStorage.getItem('design_tool.resume_design_id');
      if (resumeId) {
        localStorage.removeItem('design_tool.resume_design_id');
        const parsed = Number(resumeId);
        if (Number.isFinite(parsed) && parsed > 0) {
          openDesign({ id: parsed });
        }
      }
    } catch (err) {

    }
  }, []);

  // Generate high-resolution export (4048x2016) from design data
  /**
   * Renders the design to a high-resolution image and returns the dataURL.
   * The grass background is included. The caller decides what to do with the dataURL.
   */
  const generateHighResolutionExport = (designData: any, opts?: { format?: 'png' | 'jpg'; size?: 'small' | 'large'; quality?: number }): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        const Konva = (await import('konva')).default;

        const size = opts?.size || 'large';
        const exportWidth  = size === 'small' ? 2024 : 4048;
        const exportHeight = size === 'small' ? 1008 : 2016;

        const stage = new Konva.Stage({
          container: document.createElement('div'),
          width: exportWidth,
          height: exportHeight,
        });

        const backgroundLayer = new Konva.Layer();
        stage.add(backgroundLayer);

        /** Shared render logic used by both grass-loaded and grass-error paths */
        const renderAndResolve = async () => {
          const contentLayer = new Konva.Layer();
          stage.add(contentLayer);

          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          if (designData.elements && Array.isArray(designData.elements)) {
            designData.elements.forEach((element: any) => {
              const x = element.x || 0;
              const y = element.y || 0;
              const w = element.width  || 0;
              const h = element.height || 0;
              minX = Math.min(minX, x);     minY = Math.min(minY, y);
              maxX = Math.max(maxX, x + w); maxY = Math.max(maxY, y + h);
            });
          }
          if (minX === Infinity) { minX = 0; minY = 0; maxX = 1200; maxY = 800; }

          const designWidth  = maxX - minX;
          const designHeight = maxY - minY;
          const padding = 200;
          const scale = Math.min(
            (exportWidth  - padding * 2) / designWidth,
            (exportHeight - padding * 2) / designHeight
          );
          const offsetX = (exportWidth  - designWidth  * scale) / 2 - minX * scale;
          const offsetY = (exportHeight - designHeight * scale) / 2 - minY * scale;

          if (designData.elements && Array.isArray(designData.elements)) {
            for (const el of designData.elements) {
              await addElementToLayer(contentLayer, el, scale, scale, offsetX, offsetY);
            }
          }

          stage.batchDraw();
          await new Promise(r => setTimeout(r, 200));

          const isJpg = (opts?.format || 'png') === 'jpg';
          const dataURL = stage.toDataURL({
            mimeType: isJpg ? 'image/jpeg' : 'image/png',
            quality: typeof opts?.quality === 'number' ? opts.quality : (isJpg ? 0.92 : 1),
            pixelRatio: 1,
          });

          stage.destroy();
          resolve(dataURL);
        };

        const grassImg = new Image();
        grassImg.crossOrigin = 'anonymous';
        grassImg.onload = async () => {
          backgroundLayer.add(new Konva.Image({
            image: grassImg, x: 0, y: 0, width: exportWidth, height: exportHeight,
          }));
          await renderAndResolve();
        };
        grassImg.onerror = async () => {
          backgroundLayer.add(new Konva.Rect({
            x: 0, y: 0, width: exportWidth, height: exportHeight, fill: '#f5f5f5',
          }));
          await renderAndResolve();
        };
        grassImg.src = '/grass_bg.jpg';

      } catch (err) {
        reject(err);
      }
    });
  };

  // Helper function to add design elements to Konva layer
  const addElementToLayer = async (layer: any, element: any, scaleX: number, scaleY: number, offsetX: number = 0, offsetY: number = 0) => {
    const Konva = (await import('konva')).default;

    const scaledX = (element.x || 0) * scaleX + offsetX;
    const scaledY = (element.y || 0) * scaleY + offsetY;
    const scaledWidth = (element.width || 0) * scaleX;
    const scaledHeight = (element.height || 0) * scaleY;

    switch (element.type) {
      case 'tablet':
      case 'base':
        const rect = new Konva.Rect({
          x: scaledX,
          y: scaledY,
          width: scaledWidth,
          height: scaledHeight,
          fill: element.properties?.texture || '#8b8b8b',
          stroke: '#666',
          strokeWidth: 2 * Math.min(scaleX, scaleY),
          cornerRadius: 8 * Math.min(scaleX, scaleY),
        });
        layer.add(rect);
        break;

      case 'artwork':

        if (element.properties?.src) {
          try {

            const img = new Image();
            img.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
              img.onload = () => {

                resolve(undefined);
              };
              img.onerror = (error) => {

                reject(error);
              };
              img.src = element.properties.src;
            });

            const artworkImg = new Konva.Image({
              image: img,
              x: scaledX,
              y: scaledY,
              width: scaledWidth,
              height: scaledHeight,
              rotation: (element.rotation || 0) * Math.PI / 180,
            });
            layer.add(artworkImg);

          } catch (error) {

          }
        } else {

        }
        break;

      case 'text':





        const text = new Konva.Text({
          x: scaledX,
          y: scaledY,
          width: scaledWidth,
          height: scaledHeight,
          text: element.properties?.content || element.properties?.text || '',
          fontSize: (element.properties?.fontSize || 16) * Math.min(scaleX, scaleY),
          fontFamily: element.properties?.fontFamily || 'Times New Roman',
          fill: element.properties?.color || element.properties?.fill || '#000000',
          align: element.properties?.align || 'left',
          verticalAlign: element.properties?.verticalAlign || 'top',
          rotation: (element.rotation || 0) * Math.PI / 180,
        });
        layer.add(text);

        break;

      default:

    }
  };

  const handleDownloadDesign = (d: any) => {
    // Close the right panel and open a centered export modal via global event
    try { handleClose(); } catch { }
    try { window.dispatchEvent(new CustomEvent('export:open', { detail: { designId: d.id } })); } catch { }
  };

  const handleEmailDesign = (design: any) => {
    if (!design) return;
    setEmailModalDesign(design);
    setEmailToAddress('');
    setEmailMessage('');
    setEmailModalOpen(true);
  };

  const handleSendEmailDesign = async () => {
    if (!emailModalDesign || !emailToAddress.trim()) return;
    setEmailSending(true);
    try {
      const resp = await fetch(buildApiUrl('/api/contact/email-design'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: emailToAddress.trim(),
          design_name: emailModalDesign.name || 'Untitled Design',
          design_id: emailModalDesign.id ?? 0,
          message: emailMessage.trim(),
        }),
      });
      const data = await resp.json();
      if (resp.ok && data?.ok) {
        setEmailModalOpen(false);
        showSuccess('Email Sent', `Design summary sent to ${emailToAddress.trim()}`);
      } else {
        showError('Email Failed', data?.error || 'Failed to send email. Please try again.');
      }
    } catch (e: any) {
      showError('Email Failed', e?.message || 'Network error. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const performDownload = async () => {
    const d = downloadDesign;
    if (!d) return;
    try {
      let designData: any = d.design_json || null;
      let name = d.name || 'design';
      let thumbUrl = d.thumbnail_url || null;

      try {
        const r = await fetch(buildApiUrl(`/api/designs/${d.id}`), { headers: { ...authHeaders() } });
        const j = await r.json();
        if (r.ok && j?.ok) {
          designData = typeof j.design?.design_json === 'string' ? JSON.parse(j.design.design_json) : (j.design?.design_json || designData);
          name = j.design?.name || name;
          thumbUrl = j.design?.thumbnail_url || thumbUrl;
        }
      } catch { }

      // KPRO bundle
      if (downloadFormat === 'kpro') {
        try {
          const bundle = {
            type: 'kpro',
            __kpro: true,
            version: 1,
            app: 'Monu Design',
            savedAt: new Date().toISOString(),
            design: designData || {},
            background: designData?.background || null,
            graniteMeta: designData?.selectedGraniteMeta || undefined,
            graniteImage: null
          } as any;
          const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/x-kpro+json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${name}.kpro`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setDownloadOpen(false);
          return;
        } catch (err) {
          showError('Export Error', 'Failed to export .kpro');
          return;
        }
      }

      // Render image dataURL specifically for this saved design
      let dataURL = '';
      if (designData && Array.isArray(designData.elements)) {
        try {
          const graniteConfig = (window as any).__appGraniteConfig;
          const designMode = (window as any).__appDesignMode || '';
          dataURL = await renderProductionFromDesignJson300DPI(
            designData,
            graniteConfig,
            designMode,
            {
              format: downloadFormat === 'jpg' ? 'jpeg' : 'png',
              quality: downloadFormat === 'jpg' ? 0.88 : 1,
              targetDPI: downloadSize === 'small' ? 150 : 300
            }
          );
        } catch { }
      }

      if (!dataURL && thumbUrl) {
        dataURL = thumbUrl;
      }

      if (!dataURL && designData) {
        dataURL = await generateHighResolutionExport(designData, { format: downloadFormat as any, size: downloadSize });
      }

      if (!dataURL) {
        showError('Download Error', 'No design image available to export.');
        return;
      }

      if (downloadFormat === 'pdf') {
        await createCompressedPdf(dataURL, null, `${name}.pdf`, 4800000);
        setDownloadOpen(false);
        return;
      }

      const ext = downloadFormat === 'jpg' ? 'jpg' : 'png';
      const compressed = await compressDataUrlToLimit(dataURL, 4800000);
      const link = document.createElement('a');
      link.download = `${name}.${ext}`;
      link.href = compressed || dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadOpen(false);
      return;
    } catch (e: any) {
      showError('Download Error', e?.message || 'Failed to download');
    }
  };


  const resetContactForm = () => {
    setContactForm({
      name: '',
      email: '',
      phone: '',
      message: '',
    });
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
    setContactSuccess(null);
  };

  const handleContactSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setContactSubmitting(true);
    setTimeout(() => {
      setContactSubmitting(false);
      setContactSuccess('Thank you! A memorial specialist will reach out shortly.');
      resetContactForm();
    }, 900);
  };

  const handleClose = () => {
    onClose();
    setContactModalOpen(false);
    setContactSuccess(null);
    // Reset to login view when closing
    setTimeout(() => setViewMode('login'), 300);
  };

  const switchToCreateAccount = () => {
    setLoginForgotIntent({ active: false });
    setViewMode('createAccount');
  };

  const switchToLogin = () => {
    setLoginForgotIntent({ active: false });
    setViewMode('login');
  };

  if (!isOpen) return null;

  const panelUi = (
    <Overlay $isOpen={isOpen}>
      <PanelContainer $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderTitle>
            {viewMode === 'profile' ? 'My Account' : viewMode === 'createAccount' ? 'Sign Up' : viewMode === 'designs' ? 'My Designs' : 'Sign In'}
          </HeaderTitle>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        {viewMode !== 'profile' && viewMode !== 'designs' && (
          <TabContainer>
            <Tab
              active={viewMode === 'login'}
              onClick={switchToLogin}
            >
              Sign In
            </Tab>
            <Tab
              active={viewMode === 'createAccount'}
              onClick={switchToCreateAccount}
            >
              Create Account
            </Tab>
          </TabContainer>
        )}

        <Content>
          {viewMode === 'login' ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: '0 0 auto' }}>
                <Login
                  onSwitchToCreateAccount={switchToCreateAccount}
                  startForgotPassword={loginForgotIntent.active}
                  forgotPasswordEmailHint={loginForgotIntent.email}
                  onConsumedForgotPasswordIntent={() => setLoginForgotIntent({ active: false })}
                />
              </div>
              <div style={{ marginTop: 'auto' }}>
                <SupportSection>
                  <SupportHeading>Need help finalizing a memorial?</SupportHeading>
                  <SupportDescription>
                    Our consultants can assist with artwork approvals, inscriptions, or pricing.
                    Send us a note and we’ll respond within one business day.
                  </SupportDescription>
                  <SupportButton onClick={() => {
                    setContactModalOpen(true);
                    setContactSuccess(null);
                  }}>
                    Contact Us
                  </SupportButton>
                </SupportSection>
              </div>
            </div>
          ) : (
            <ScrollArea>
              {viewMode === 'profile' ? (
                <div style={{ padding: '20px 16px 24px', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 18, background: '#f8fafc' }}>
                  <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', borderRadius: 20, padding: 18, color: '#fff', boxShadow: '0 24px 48px rgba(15,23,42,0.35)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 54, height: 54, borderRadius: 14, background: '#1e3a8a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, textTransform: 'uppercase' }}>
                        {String((profile?.user_name || profile?.first_name || profile?.last_name || ''))
                          .trim()
                          .charAt(0)
                          .toUpperCase() || '•'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{profile?.user_name || [profile?.first_name, profile?.middle_name, profile?.last_name].filter(Boolean).join(' ') || 'My Account'}</div>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>{profile?.email || ''}</div>
                      </div>
                      <div style={{ padding: '6px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.18)', fontSize: 12, letterSpacing: 0.3 }}>Profile</div>
                    </div>
                  </div>

                  <div style={{ background: '#fff', borderRadius: 20, padding: 18, boxShadow: '0 18px 48px rgba(15,23,42,0.12)', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Contact Details</div>
                    <NameRow />
                    <EmailRow />
                    <PhoneRow />
                    <AddressRow />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
                    {profile?.role !== 'dealer_salesperson' && (
                      <button
                        onClick={() => { window.location.href = '/customer/dashboard'; }}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                          color: '#fff',
                          borderRadius: 12,
                          cursor: 'pointer',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 10,
                          boxShadow: '0 14px 30px rgba(37,99,235,0.35)'
                        }}
                      >
                        <ShoppingCart size={18} />
                        My Cart
                      </button>
                    )}
                    <button
                      onClick={() => {
                        // Call backend logout
                        void fetch(buildApiUrl('/api/auth/logout'), {
                          method: 'POST',
                          headers: { ...authHeaders() },
                          keepalive: true
                        }).catch(() => { });

                        try {
                          localStorage.removeItem('auth_token');
                          localStorage.removeItem('auth_user');
                          localStorage.setItem('logout_signal', Date.now().toString());
                        } catch { }
                        try { sessionStorage.removeItem('auth_token'); sessionStorage.removeItem('auth_user'); } catch { }
                        setUser(null);
                        setViewMode('login');
                        window.dispatchEvent(new Event('auth:logout'));
                      }}
                      style={{ flex: 1, padding: '10px 16px', border: 'none', background: '#dc2626', color: '#fff', borderRadius: 12, cursor: 'pointer', fontWeight: 700, boxShadow: '0 12px 28px rgba(220,38,38,0.35)' }}
                    >
                      Log out
                    </button>
                  </div>

                  <div style={{ marginTop: 'auto' }}>
                    <SupportSection style={{ borderRadius: 18 }}>
                      <SupportHeading>Need help finalizing a memorial?</SupportHeading>
                      <SupportDescription>
                        Our consultants can assist with artwork approvals, inscriptions, or pricing.
                        Send us a note and we’ll respond within one business day.
                      </SupportDescription>
                      <SupportButton onClick={() => {
                        setContactModalOpen(true);
                        setContactSuccess(null);
                      }}>
                        Contact Us
                      </SupportButton>
                    </SupportSection>
                  </div>
                </div>
              ) : viewMode === 'designs' ? (
                <div style={{ padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{designs.length} item{designs.length === 1 ? '' : 's'}</div>
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                      <button
                        onClick={deleteAllDesigns}
                        disabled={loading || designs.length === 0}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #e53e3e',
                          color: '#e53e3e',
                          borderRadius: 6,
                          background: '#fff',
                          cursor: loading || designs.length === 0 ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          fontWeight: 700,
                          display: designs.length === 0 ? 'none' : 'inline-block'
                        }}
                        title="Delete all designs"
                      >
                        Delete all
                      </button>
                    </div>
                  </div>
                  {loading && null}
                  {error && <div style={{ padding: 12, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 5 }}>{error}</div>}
                  {!loading && !error && (
                    designs.length === 0 ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 'calc(100vh - 140px)',
                        color: '#64748b',
                        textAlign: 'center',
                        padding: 16
                      }}>
                        <div>
                          <div style={{ display: 'inline-flex', width: 48, height: 48, borderRadius: 12, background: '#f1f5f9', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="14" rx="2" ry="2"></rect>
                              <path d="M3 8h18"></path>
                              <path d="M7 4v4"></path>
                            </svg>
                          </div>
                          <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>No saved designs yet</div>
                          <div style={{ fontSize: 13 }}>Click Save to store your current design and it will appear here.</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, overflowX: 'hidden' }}>
                        {designs.map((d: any) => {
                          const stageMeta = getDesignStageMeta(d);
                          const isDealerSalesperson = (profile?.role === 'dealer_salesperson');
                          const canAddToCart = !isDealerSalesperson && stageMeta.key === 'design_created';
                          return (
                            <div
                              key={d.id}
                              style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: 10,
                                background: '#fff',
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                                transition: 'all 0.2s ease',
                                overflow: 'visible',
                                position: 'relative'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                              onClick={() => {
                                if (editingId === d.id) return;
                                openDesign(d);
                              }}
                            >
                              {/* Thumbnail Section */}
                              <div style={{
                                width: '100%',
                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 5,
                                borderBottom: '1px solid #f1f5f9'
                              }}>
                                {d.thumbnail_url ? (
                                  <img
                                    src={d.thumbnail_url}
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '100%',
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                    onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#94a3b8',
                                    fontSize: 24
                                  }}>
                                    <div style={{
                                      fontSize: 36,
                                      marginBottom: 8,
                                      opacity: 0.7
                                    }}>🎨</div>
                                    <div style={{
                                      fontSize: 12,
                                      textAlign: 'center',
                                      fontWeight: 500,
                                      color: '#64748b'
                                    }}>Design Preview</div>
                                  </div>
                                )}
                              </div>

                              {/* Content Section */}
                              {editingId === d.id ? (
                                <div style={{ padding: '16px' }} onClick={(e) => e.stopPropagation()}>
                                  <input
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    autoFocus
                                    style={{
                                      width: '100%',
                                      padding: '10px 12px',
                                      border: '2px solid #3182ce',
                                      borderRadius: 5,
                                      marginBottom: 12,
                                      fontSize: 14,
                                      outline: 'none',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                      onClick={() => renameDesign(d, editingName)}
                                      style={{
                                        padding: '8px 16px',
                                        border: 'none',
                                        background: '#3182ce',
                                        color: '#fff',
                                        borderRadius: 5,
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        transition: 'background 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.background = '#2c5aa0'}
                                      onMouseLeave={(e) => e.currentTarget.style.background = '#3182ce'}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => { setEditingId(null); }}
                                      style={{
                                        padding: '8px 16px',
                                        border: '1px solid #e2e8f0',
                                        background: '#fff',
                                        borderRadius: 5,
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f8fafc';
                                        e.currentTarget.style.borderColor = '#cbd5e0';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#fff';
                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* Design Info */}
                                  <div style={{ padding: '12px 12px 8px 12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <div style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: '#1a202c',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        marginBottom: 4,
                                        flex: 1
                                      }}>
                                        {d.name || 'Untitled'}
                                      </div>
                                    </div>
                                    <div style={{
                                      fontSize: 11,
                                      color: '#64748b',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                      </svg>
                                      <span>{d.updated_at || ''}</span>
                                    </div>
                                  </div>

                                  {/* Action Buttons converted to compact icon row */}
                                  <div style={{ padding: '0 12px 12px 12px' }} onClick={(e) => e.stopPropagation()}>
                                    <ActionIconRow>
                                      <ActionIconItem
                                        title="Edit design"
                                        onClick={(e) => { e.stopPropagation(); openDesign(d); }}
                                      >
                                        <Edit3 />
                                      </ActionIconItem>
                                      <ActionIconItem
                                        title="Rename design"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingId(d.id);
                                          setEditingName(d.name || '');
                                        }}
                                      >
                                        <Type />
                                      </ActionIconItem>
                                      <ActionIconItem
                                        title="Download design"
                                        $variant="success"
                                        onClick={(e) => { e.stopPropagation(); handleDownloadDesign(d); }}
                                      >
                                        <Download />
                                      </ActionIconItem>
                                      <ActionIconItem
                                        title="Email design summary"
                                        onClick={(e) => { e.stopPropagation(); handleEmailDesign(d); }}
                                      >
                                        <Send />
                                      </ActionIconItem>
                                      <ActionIconItem
                                        title="Delete design"
                                        $variant="danger"
                                        onClick={(e) => { e.stopPropagation(); deleteDesign(d); }}
                                      >
                                        <Trash2 />
                                      </ActionIconItem>
                                      {canAddToCart && (
                                        <ActionIconItem
                                          title="Create quotation"
                                          $variant="primary"
                                          disabled={d.status === 'quoted'}
                                          onClick={(e) => { e.stopPropagation(); handleCreateQuotation(d); }}
                                        >
                                          <FileText />
                                        </ActionIconItem>
                                      )}
                                    </ActionIconRow>
                                    <StatusBadge $color={stageMeta.color} $bg={stageMeta.background}>
                                      <StatusDot $color={stageMeta.color} />
                                      {stageMeta.label}
                                    </StatusBadge>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
                    <CreateAccount
                      onSwitchToLogin={(opts) => {
                        if (opts?.forgotPassword) {
                          setLoginForgotIntent({ active: true, email: opts.email });
                        } else {
                          setLoginForgotIntent({ active: false });
                        }
                        setViewMode('login');
                      }}
                    />
                    <div style={{ marginTop: 'auto' }}>
                      <SupportSection>
                        <SupportHeading>Need help finalizing a memorial?</SupportHeading>
                        <SupportDescription>
                          Our consultants can assist with artwork approvals, inscriptions, or pricing.
                          Send us a note and we’ll respond within one business day.
                        </SupportDescription>
                        <SupportButton onClick={() => {
                          setContactModalOpen(true);
                          setContactSuccess(null);
                        }}>
                          Contact Us
                        </SupportButton>
                      </SupportSection>
                    </div>
                  </div>
                </>
              )}
            </ScrollArea>
          )}
        </Content>
      </PanelContainer>

      {contactModalOpen && (
        <ContactModalOverlay onClick={closeContactModal}>
          <ContactModalCard onClick={(e) => e.stopPropagation()}>
            <ContactModalHeader>
              <span>Contact Monu Design</span>
              <ContactCloseButton onClick={closeContactModal}>×</ContactCloseButton>
            </ContactModalHeader>
            <ContactForm onSubmit={handleContactSubmit}>
              {contactSuccess && <ContactSuccess>{contactSuccess}</ContactSuccess>}

              <ContactField>
                <ContactLabel htmlFor="contact-name">Full Name</ContactLabel>
                <ContactInput
                  id="contact-name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Jane Memorial"
                  required
                />
              </ContactField>

              <ContactField>
                <ContactLabel htmlFor="contact-email">Email</ContactLabel>
                <ContactInput
                  id="contact-email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="you@example.com"
                  required
                />
              </ContactField>

              <ContactField>
                <ContactLabel htmlFor="contact-phone">Phone (optional)</ContactLabel>
                <ContactInput
                  id="contact-phone"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 987-6543"
                />
              </ContactField>

              <ContactField>
                <ContactLabel htmlFor="contact-message">Message</ContactLabel>
                <ContactTextarea
                  id="contact-message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Tell us about the memorial assistance you need…"
                  required
                />
              </ContactField>

              <ContactActions>
                <ContactSecondaryButton type="button" onClick={closeContactModal}>
                  Cancel
                </ContactSecondaryButton>
                <ContactPrimaryButton type="submit" disabled={contactSubmitting}>
                  {contactSubmitting ? 'Sending…' : 'Submit'}
                </ContactPrimaryButton>
              </ContactActions>
            </ContactForm>
          </ContactModalCard>
        </ContactModalOverlay>
      )}

      {/* Popup */}
      <Popup
        isOpen={popupState.isOpen}
        onClose={hidePopup}
        type={popupState.type}
        title={popupState.title}
        message={popupState.message}
        confirmText={popupState.confirmText}
        cancelText={popupState.cancelText}
        onConfirm={popupState.onConfirm}
        onCancel={popupState.onCancel}
        showCancel={popupState.showCancel}
      />

      {/* Download Modal */}
      {downloadOpen && downloadDesign && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 4000 }} onClick={() => setDownloadOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', right: 18, top: 70, width: 360, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 16px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Exporting 1 design</span>
              <button onClick={() => setDownloadOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 12 }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, marginBottom: 6 }}>Export</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'png' && downloadSize === 'small' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'png' && downloadSize === 'small'} onChange={() => { setDownloadFormat('png'); setDownloadSize('small'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>PNG Small</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>For web</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'png' && downloadSize === 'large' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'png' && downloadSize === 'large'} onChange={() => { setDownloadFormat('png'); setDownloadSize('large'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>PNG Large</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Original + Settings</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'jpg' && downloadSize === 'small' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'jpg' && downloadSize === 'small'} onChange={() => { setDownloadFormat('jpg'); setDownloadSize('small'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>JPG Small</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>For web</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'jpg' && downloadSize === 'large' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'jpg' && downloadSize === 'large'} onChange={() => { setDownloadFormat('jpg'); setDownloadSize('large'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>JPG Large</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Original + Settings</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'pdf' && downloadSize === 'small' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'pdf' && downloadSize === 'small'} onChange={() => { setDownloadFormat('pdf'); setDownloadSize('small'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>PDF Small</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>For email / web</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'pdf' && downloadSize === 'large' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'pdf' && downloadSize === 'large'} onChange={() => { setDownloadFormat('pdf'); setDownloadSize('large'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>PDF Large</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Print quality</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid ' + (downloadFormat === 'kpro' ? '#60a5fa' : '#e2e8f0'), borderRadius: 8, cursor: 'pointer' }}>
                  <input type="radio" name="fmt" checked={downloadFormat === 'kpro'} onChange={() => { setDownloadFormat('kpro'); }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1f2937' }}>.kpro (Editable)</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Re-open later to continue editing</div>
                  </div>
                </label>
              </div>

              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={performDownload} style={{ padding: '10px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>Download</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Design Modal */}
      {emailModalOpen && emailModalDesign && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 4200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEmailModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(420px, 90vw)', background: '#fff', borderRadius: 14, boxShadow: '0 16px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>Email Design Summary</div>
              <button onClick={() => setEmailModalOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, color: '#475569' }}>
                Send a summary of <strong>{emailModalDesign.name || 'this design'}</strong> to any email address.
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Recipient Email *</label>
                <input
                  type="email"
                  value={emailToAddress}
                  onChange={(e) => setEmailToAddress(e.target.value)}
                  placeholder="e.g. client@example.com"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Message (optional)</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Add a personal note…"
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button onClick={() => setEmailModalOpen(false)} style={{ padding: '9px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Cancel</button>
                <button
                  onClick={handleSendEmailDesign}
                  disabled={emailSending || !emailToAddress.trim()}
                  style={{ padding: '9px 18px', background: emailSending || !emailToAddress.trim() ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: emailSending || !emailToAddress.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {emailSending ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Overlay>
  );

  // Render in a portal to avoid parent stacking contexts (e.g., transforms) causing
  // the Designer preloader overlay to appear above the panel even with higher z-index.
  if (typeof document !== 'undefined') {
    return createPortal(panelUi, document.body);
  }
  return panelUi;
};

export default RightPanel;